'use client';

import { useRef, useState } from 'react';
import { BriefSection, Study } from '@/lib/study';
import { T } from '@/lib/tokens';
import { extractJSON, run } from '@/lib/ai';
import { briefSections } from '@/lib/prompts';
import { Btn } from '../Btn';
import { Nav, StepHead, StepWrap } from '../StepShell';
import { ParticipantPreviewModal } from '../ParticipantPreviewModal';

type Props = {
  data: Study;
  setData: (d: Study) => void;
  onBack: () => void;
  onNext: () => void;
};

const BRIEF_PRESETS = [
  { title: 'Purpose of the study', hint: 'Why are you doing this study?' },
  { title: "What you'll be asked to do", hint: 'Interview format and what to expect.' },
  { title: 'Risks & benefits', hint: 'Sensitive topics, direct benefits.' },
  { title: 'How we handle your data', hint: 'Storage, access, retention.' },
  { title: 'Voluntary participation', hint: 'They can stop at any time.' },
  { title: 'Compensation', hint: 'Gift card, payment, credit.' },
  { title: 'Eligibility criteria', hint: 'Who can take part? List any age, location, or experience requirements.' },
  { title: 'Funding & sponsors', hint: 'Who funds this research.' },
  { title: 'IRB approval', hint: 'Ethics review information.' },
];

export function Step10Brief({ data, setData, onBack, onNext }: Props) {
  const [aiLoading, setAiLoading] = useState(false);
  const [showPresets, setShowPresets] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const sections = Array.isArray(data.brief) ? data.brief : [];
  // Counter for new brief section IDs — keeps them unique without an impure call.
  const nextIdRef = useRef(1);
  const newId = () => `b-new-${nextIdRef.current++}`;

  const update = (id: string, patch: Partial<BriefSection>) =>
    setData({ ...data, brief: sections.map((s) => (s.id === id ? { ...s, ...patch } : s)) });

  const remove = (id: string) => setData({ ...data, brief: sections.filter((s) => s.id !== id) });

  const move = (idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= sections.length) return;
    const arr = [...sections];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setData({ ...data, brief: arr });
  };

  const addCustom = () => {
    setData({
      ...data,
      brief: [...sections, { id: newId(), title: '', hint: '', body: '', custom: true }],
    });
  };

  const addPreset = (p: { title: string; hint: string }) => {
    setData({
      ...data,
      brief: [...sections, { id: newId(), title: p.title, hint: p.hint, body: '' }],
    });
    setShowPresets(false);
  };

  const generateAll = async () => {
    setAiLoading(true);
    try {
      const text = await run(briefSections(data, sections));
      const bodies = extractJSON<string[]>(text);
      if (bodies) {
        setData({
          ...data,
          brief: sections.map((s, i) => ({ ...s, body: bodies[i] || s.body })),
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <StepWrap>
      <StepHead
        label="Step 11 — Participant Brief"
        title="What should participants know before they start?"
        subtitle="In standalone mode, participants see these sections on the welcome page before consenting. Customize the defaults, reorder, remove what doesn't apply, or add your own sections."
      />
      {showPreview && <ParticipantPreviewModal study={data} onClose={() => setShowPreview(false)} />}

      {/* ── Section 1: Quick facts ── */}
      <Section title="Quick facts strip" description="Shown at the top of the welcome page. Click a value to edit.">
        <div
          style={{
            background: T.white,
            border: `1px solid ${T.creamBorder}`,
            borderRadius: 12,
            padding: '16px 20px',
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 16,
          }}
        >
          {/* Length — editable number */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span style={{ fontSize: 18, marginBottom: 2 }}>⏱</span>
            <span style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.espressoLight, fontWeight: 600 }}>Length</span>
            <DurationPill
              value={data.duration}
              onChange={(v) => setData({ ...data, duration: v })}
            />
          </div>

          {/* Format — fixed */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span style={{ fontSize: 18, marginBottom: 2 }}>🎙</span>
            <span style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.espressoLight, fontWeight: 600 }}>Format</span>
            <span style={{ fontSize: 15, color: T.espresso, fontWeight: 500 }}>Voice with AI</span>
          </div>

          {/* Compensation — editable text */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span style={{ fontSize: 18, marginBottom: 2 }}>🎁</span>
            <span style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.espressoLight, fontWeight: 600 }}>Compensation</span>
            <input
              value={data.compensation}
              onChange={(e) => setData({ ...data, compensation: e.target.value })}
              placeholder="e.g. $20 gift card"
              style={inlineInputStyle}
              onFocus={(e) => { e.target.style.borderColor = T.terra; e.target.style.background = T.white; }}
              onBlur={(e) => { e.target.style.borderColor = T.creamBorder; e.target.style.background = T.cream; }}
            />
          </div>
        </div>
      </Section>

      {/* ── Section 2: Participant brief ── */}
      <Section
        title="Participant brief"
        description="Customize the sections below, or let AI draft them from your study goal."
        action={
          <Btn small onClick={generateAll} disabled={aiLoading || sections.length === 0}>
            {aiLoading ? 'Drafting…' : 'Draft with AI'}
          </Btn>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {sections.map((s, i) => (
            <div
              key={s.id}
              style={{
                background: T.white,
                border: `1.5px solid ${T.creamBorder}`,
                borderRadius: 12,
                padding: '16px 18px',
                display: 'flex',
                gap: 12,
                alignItems: 'flex-start',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 4, flexShrink: 0 }}>
                <button
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  style={{ background: 'none', border: 'none', cursor: i === 0 ? 'default' : 'pointer', color: i === 0 ? T.creamBorder : T.espressoLight, fontSize: 13, padding: '2px 4px', lineHeight: 1 }}
                >▲</button>
                <button
                  onClick={() => move(i, 1)}
                  disabled={i === sections.length - 1}
                  style={{ background: 'none', border: 'none', cursor: i === sections.length - 1 ? 'default' : 'pointer', color: i === sections.length - 1 ? T.creamBorder : T.espressoLight, fontSize: 13, padding: '2px 4px', lineHeight: 1 }}
                >▼</button>
              </div>
              <span className="serif" style={{ fontSize: 14, color: T.terra, letterSpacing: '0.05em', paddingTop: 6, minWidth: 24 }}>
                {String(i + 1).padStart(2, '0')}
              </span>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input
                  value={s.title}
                  onChange={(e) => update(s.id, { title: e.target.value })}
                  placeholder="Section title…"
                  style={{ width: '100%', border: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 15, fontWeight: 600, color: T.espresso, outline: 'none', padding: '2px 0' }}
                />
                <textarea
                  value={s.body}
                  onChange={(e) => update(s.id, { body: e.target.value })}
                  rows={3}
                  placeholder={s.hint ? `e.g. ${s.hint}` : 'Write a short paragraph…'}
                  style={{ width: '100%', resize: 'vertical', border: `1px solid ${T.creamBorder}`, borderRadius: 8, padding: '10px 14px', fontFamily: 'inherit', fontSize: 14, lineHeight: 1.6, color: T.espresso, background: T.cream, outline: 'none' }}
                  onFocus={(e) => (e.target.style.borderColor = T.terra)}
                  onBlur={(e) => (e.target.style.borderColor = T.creamBorder)}
                />
              </div>
              <button onClick={() => remove(s.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.espressoLight, fontSize: 20, padding: 4, lineHeight: 1, flexShrink: 0 }} title="Remove">×</button>
            </div>
          ))}

          {!showPresets ? (
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowPresets(true)} style={{ flex: 1, border: `1.5px dashed ${T.creamBorder}`, borderRadius: 12, padding: '14px 18px', background: 'none', cursor: 'pointer', color: T.espressoLight, fontSize: 14, fontFamily: 'inherit' }}>
                + Add a section
              </button>
              <button onClick={addCustom} style={{ border: `1.5px dashed ${T.creamBorder}`, borderRadius: 12, padding: '14px 18px', background: 'none', cursor: 'pointer', color: T.espressoLight, fontSize: 14, fontFamily: 'inherit' }}>
                + Custom
              </button>
            </div>
          ) : (
            <div style={{ background: T.white, border: `1.5px solid ${T.terra}`, borderRadius: 12, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: T.espresso }}>Pick a preset, or write your own</span>
                <button onClick={() => setShowPresets(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.espressoLight, fontSize: 18, padding: 0, lineHeight: 1 }}>×</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {BRIEF_PRESETS.filter((p) => !sections.some((s) => s.title === p.title)).map((p) => (
                  <button key={p.title} onClick={() => addPreset(p)} style={{ textAlign: 'left', background: T.cream, border: `1px solid ${T.creamBorder}`, borderRadius: 8, padding: '10px 14px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 14, fontWeight: 500, color: T.espresso }}>{p.title}</span>
                    <span style={{ fontSize: 12, color: T.espressoLight }}>{p.hint}</span>
                  </button>
                ))}
                <button onClick={() => { addCustom(); setShowPresets(false); }} style={{ textAlign: 'left', background: 'transparent', border: `1px dashed ${T.creamBorder}`, borderRadius: 8, padding: '10px 14px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, color: T.terra, fontWeight: 500 }}>
                  + Custom section (write your own title)
                </button>
              </div>
            </div>
          )}
        </div>
      </Section>

      {/* ── Section 3: Research team ── */}
      <Section title="Research team" description="Always shown below the brief on the welcome page.">
        <div
          style={{
            background: T.terraLight,
            border: `1px solid ${T.terraMid}`,
            borderRadius: 12,
            padding: '16px 18px',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              value={data.researcher}
              onChange={(e) => setData({ ...data, researcher: e.target.value })}
              placeholder="Researcher name (required)"
              style={{ ...inputStyle, flex: 1, border: `1px solid ${T.terraMid}`, background: T.white }}
              onFocus={(e) => (e.target.style.borderColor = T.terra)}
              onBlur={(e) => (e.target.style.borderColor = T.terraMid)}
            />
            <input
              value={data.affiliation}
              onChange={(e) => setData({ ...data, affiliation: e.target.value })}
              placeholder="Affiliation"
              style={{ ...inputStyle, flex: 1, border: `1px solid ${T.terraMid}`, background: T.white }}
              onFocus={(e) => (e.target.style.borderColor = T.terra)}
              onBlur={(e) => (e.target.style.borderColor = T.terraMid)}
            />
          </div>
          <input
            value={data.contactEmail}
            onChange={(e) => setData({ ...data, contactEmail: e.target.value })}
            placeholder="Contact email (required)"
            type="email"
            style={{ ...inputStyle, border: `1px solid ${T.terraMid}`, background: T.white }}
            onFocus={(e) => (e.target.style.borderColor = T.terra)}
            onBlur={(e) => (e.target.style.borderColor = T.terraMid)}
          />
          <input
            value={data.irb}
            onChange={(e) => setData({ ...data, irb: e.target.value })}
            placeholder="IRB / ethics approval (optional)"
            style={{ ...inputStyle, border: `1px solid ${T.terraMid}`, background: T.white }}
            onFocus={(e) => (e.target.style.borderColor = T.terra)}
            onBlur={(e) => (e.target.style.borderColor = T.terraMid)}
          />
          <PreviewLabel />
          <div style={{ background: T.white, borderRadius: 10, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 15, color: T.espresso, fontWeight: 500 }}>
              {data.researcher || data.affiliation
                ? [data.researcher, data.affiliation].filter(Boolean).join(', ')
                : <span style={{ color: T.espressoLight, fontStyle: 'italic' }}>Researcher name, Affiliation</span>}
            </span>
            <span style={{ fontSize: 14, color: T.espressoMid }}>
              Questions? Email{' '}
              <span style={{ color: T.terra, textDecoration: 'underline' }}>{data.contactEmail || <span style={{ fontStyle: 'italic' }}>contact@email.com</span>}</span>
            </span>
            {data.irb && <span style={{ fontSize: 12, color: T.espressoLight, fontStyle: 'italic', marginTop: 4 }}>{data.irb}</span>}
          </div>
        </div>
      </Section>

      {/* ── Section 4: Your consent ── */}
      <Section title="Your consent" description="Fixed items — always shown, no setup needed.">
        <div
          style={{
            background: T.white,
            border: `1.5px solid ${T.creamBorder}`,
            borderRadius: 12,
            padding: '16px 18px',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,

            pointerEvents: 'none',
          }}
        >
          {[
            "I'm 18 or older and I'm taking part in this study voluntarily.",
            "I've read the information above and understand that my conversation will be recorded and transcribed for research purposes.",
            "I understand I can stop at any time without giving a reason.",
          ].map((label) => (
            <div key={label} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <span style={{ color: T.terra, fontSize: 22, lineHeight: 1.1, flexShrink: 0 }}>·</span>
              <span style={{ fontSize: 14, color: T.espressoMid, lineHeight: 1.6 }}>{label}</span>
            </div>
          ))}
        </div>
      </Section>

      <Nav
        onBack={onBack}
        onNext={onNext}
        extras={
          <Btn variant="secondary" small onClick={() => setShowPreview(true)}>
            Preview participant view
          </Btn>
        }
      />
    </StepWrap>
  );
}

/* ── Shared layout helpers ── */

const inlineInputStyle: React.CSSProperties = {
  background: T.cream,
  border: `1.5px solid ${T.creamBorder}`,
  borderRadius: 6,
  outline: 'none',
  fontFamily: 'inherit',
  fontSize: 15,
  fontWeight: 500,
  color: T.espresso,
  padding: '4px 10px',
  width: '100%',
  cursor: 'text',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  border: `1px solid ${T.creamBorder}`,
  borderRadius: 8,
  padding: '10px 14px',
  fontFamily: 'inherit',
  fontSize: 14,
  color: T.espresso,
  background: T.cream,
  outline: 'none',
  boxSizing: 'border-box',
};

function Section({ title, description, action, children }: {
  title: string;
  description: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: T.espresso, fontWeight: 700 }}>
            {title}
          </span>
          <span style={{ fontSize: 13, color: T.espressoLight }}>{description}</span>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function Field({ label, style, children }: { label: string; style?: React.CSSProperties; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, ...style }}>
      <label style={{ fontSize: 12, color: T.espressoLight, fontWeight: 500 }}>{label}</label>
      {children}
    </div>
  );
}

function DurationPill({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const pillRef = useRef<HTMLDivElement>(null);
  return (
    <div
      ref={pillRef}
      style={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: 3,
        background: T.cream,
        border: `1.5px solid ${T.creamBorder}`,
        borderRadius: 6,
        padding: '4px 10px',
        width: 'fit-content',
        transition: 'border-color 0.1s',
      }}
    >
      <span style={{ fontSize: 15, color: T.espresso, fontWeight: 500 }}>~</span>
      <input
        type="number"
        min={1}
        value={value}
        onChange={(e) => onChange(Math.max(1, parseInt(e.target.value) || 1))}
        style={{ background: 'transparent', border: 'none', outline: 'none', fontFamily: 'inherit', fontSize: 15, fontWeight: 500, color: T.espresso, width: 42, padding: 0 }}
        onFocus={() => { if (pillRef.current) { pillRef.current.style.borderColor = T.terra; pillRef.current.style.background = T.white; } }}
        onBlur={() => { if (pillRef.current) { pillRef.current.style.borderColor = T.creamBorder; pillRef.current.style.background = T.cream; } }}
      />
      <span style={{ fontSize: 15, color: T.espresso, fontWeight: 500 }}>min</span>
    </div>
  );
}

function PreviewLabel() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '2px 0' }}>
      <div style={{ flex: 1, height: 1, background: T.creamBorder }} />
      <span style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.espressoLight, fontWeight: 600 }}>
        Preview
      </span>
      <div style={{ flex: 1, height: 1, background: T.creamBorder }} />
    </div>
  );
}
